import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeaderNav } from '../components/HeaderNav';

type Section = 'overview' | 'flightmode' | 'tower' | 'studio' | 'dataflow' | 'glossary';

const NAV: Array<{ id: Section; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'flightmode', label: 'Flightmode' },
  { id: 'tower', label: 'Tower' },
  { id: 'studio', label: 'Studio' },
  { id: 'dataflow', label: 'Data Flow' },
  { id: 'glossary', label: 'Glossary' },
];

function Widget({ name, desc, color }: { name: string; desc: string; color: string }) {
  return (
    <div className="flex gap-3 py-2 border-b border-tower-border/30">
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${color}`} />
      <div>
        <div className="text-sm font-mono text-tower-accent">{name}</div>
        <div className="text-xs text-tower-muted">{desc}</div>
      </div>
    </div>
  );
}

function FlowStep({ num, title, desc, color }: { num: string; title: string; desc: string; color: string }) {
  return (
    <div className="flex gap-3 items-start">
      <div className={`w-7 h-7 rounded-full ${color} flex items-center justify-center text-textPrimary text-xs font-bold shrink-0`}>{num}</div>
      <div>
        <div className="text-sm font-semibold text-tower-text">{title}</div>
        <div className="text-xs text-tower-muted">{desc}</div>
      </div>
    </div>
  );
}

export function DocsPage() {
  const [active, setActive] = useState<Section>('overview');
  const kbBaseUrl = window.location.hostname === 'app.anhdigital.ai'
    ? 'https://anhdigital.ai'
    : window.location.hostname.includes('dev.')
      ? 'https://dev.cargillai.com'
      : 'https://api.cargillai.com';

  return (
    <div className="h-screen bg-tower-bg text-tower-text flex flex-col">
      <header className="h-14 bg-tower-surface border-b border-tower-border flex items-center px-6 gap-4 shrink-0">
        <Link to="/" className="text-lg font-mono font-bold text-tower-accent tracking-wider hover:text-tower-text transition-colors">
          MAESTRO
        </Link>
        <div className="h-4 w-px bg-tower-border" />
        <span className="text-sm text-tower-muted">Documentation</span>
        <div className="flex-1" />
        <HeaderNav />
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar nav */}
        <nav className="w-48 border-r border-tower-border p-3 shrink-0 overflow-y-auto">
          {NAV.map(n => (
            <button key={n.id} onClick={() => setActive(n.id)}
              className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${active === n.id ? 'bg-tower-accent text-textOnPrimary' : 'text-tower-muted hover:text-tower-text hover:bg-tower-border/30'}`}>
              {n.label}
            </button>
          ))}
          <div className="border-t border-tower-border mt-3 pt-3">
            <a href={`${kbBaseUrl}/knowledge`} target="_blank" rel="noopener noreferrer"
              className="block px-3 py-2 text-sm text-tower-muted hover:text-tower-accent transition-colors">
              Knowledge Hub &rarr;
            </a>
            <a href={`${kbBaseUrl}/knowledge?search=journal+studio`} target="_blank" rel="noopener noreferrer"
              className="block px-3 py-2 text-xs text-tower-muted hover:text-tower-accent transition-colors">
              Doc 132: Studio Guide
            </a>
            <a href={`${kbBaseUrl}/knowledge?search=mcp+integration`} target="_blank" rel="noopener noreferrer"
              className="block px-3 py-2 text-xs text-tower-muted hover:text-tower-accent transition-colors">
              Doc 131: MCP IDE Arch
            </a>
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 max-w-4xl">
          {active === 'overview' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold text-tower-accent">Maestro Journal Dashboard</h1>
              <p className="text-sm text-tower-text leading-relaxed">
                The Maestro Journal Dashboard is a research workspace and MCP development IDE.
                It has three modes, each designed for a different phase of work:
              </p>

              <div className="grid grid-cols-3 gap-4">
                <button onClick={() => setActive('flightmode')} className="p-4 bg-tower-surface border border-primary/30 rounded-lg text-left hover:border-primary/60 transition-colors">
                  <div className="text-sm font-bold text-info mb-1">Flightmode</div>
                  <div className="text-xs text-tower-muted">Active research engagement. Monitor findings, events, budgets, and annotate in real-time.</div>
                </button>
                <button onClick={() => setActive('tower')} className="p-4 bg-tower-surface border border-warning/30 rounded-lg text-left hover:border-warning/60 transition-colors">
                  <div className="text-sm font-bold text-warning mb-1">Tower</div>
                  <div className="text-xs text-tower-muted">Orchestration overview. Visualize continuation lineage, wake conditions, and cost tracking.</div>
                </button>
                <button onClick={() => setActive('studio')} className="p-4 bg-tower-surface border border-success/30 rounded-lg text-left hover:border-success/60 transition-colors">
                  <div className="text-sm font-bold text-success mb-1">Studio</div>
                  <div className="text-xs text-tower-muted">MCP development IDE. Edit SQL queries, agent prompts, and tool definitions. Test and deploy from the browser.</div>
                </button>
              </div>

              <div className="p-4 bg-tower-surface border border-tower-border rounded-lg">
                <div className="text-sm font-semibold text-tower-accent mb-2">Two Layers of Agentic Evolution</div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="font-semibold text-info mb-1">Layer 1: Agents (consumers)</div>
                    <p className="text-tower-muted">Whisper, Max Dairy Analyst, CVIE Analyst call MCP tools to answer questions.
                      Their behavior is controlled by system prompts and tool bindings — editable in the Prompt Editor.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-warning mb-1">Layer 2: MCP Tools (data access)</div>
                    <p className="text-tower-muted">Each tool runs a versioned SQL query against Redshift or PostgreSQL.
                      The SQL is stored as a versioned artifact in Cosmos DB — editable in the SQL Editor.
                      Changes go live within 30 seconds. No code deploy needed.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {active === 'flightmode' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-info">Flightmode</h1>
              <p className="text-sm text-tower-muted">Active research monitoring. Use when a continuation is running and you want to watch findings arrive in real-time.</p>
              <div className="space-y-1">
                <Widget name="Goal Frame" desc="Current research objective, question, evidence requirements, and eligible tools. Edit controls to redirect the research." color="bg-primary" />
                <Widget name="Findings Stream" desc="Published findings with confidence scores, source citations, and the tick index that produced them. Sorted newest-first." color="bg-success" />
                <Widget name="Budget Gauge" desc="Visual gauges showing token/dollar/wall-clock spend vs caps. Broken down by Opus/Sonnet/Haiku model tiers." color="bg-warning" />
                <Widget name="Draft Viewer" desc="The working draft of the research output — the evolving document that the continuation is building across ticks." color="bg-purpleToken" />
                <Widget name="Event Log" desc="Real-time SSE stream of all events: model_call, tool_call, publish, fork, sleep, wake, budget_charge, human_signal, error." color="bg-info" />
                <Widget name="Annotation Input" desc="Send researcher signals: approve (resume a waiting continuation), redirect (change the goal), kill (stop), or add freeform annotations." color="bg-warning" />
              </div>
              <div className="p-3 bg-tower-surface border border-tower-border rounded text-xs text-tower-muted">
                <span className="text-info font-semibold">Data source:</span> Cosmos DB containers — continuations, continuation-events. SSE endpoint: GET /api/journals/{'{rootId}'}/stream
              </div>
            </div>
          )}

          {active === 'tower' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-warning">Tower</h1>
              <p className="text-sm text-tower-muted">Orchestration overview. Use when you have multiple continuations (forks, merges) and need to see the big picture.</p>
              <div className="space-y-1">
                <Widget name="Lineage Tree" desc="Visual tree showing how continuations fork and merge. Each node shows status (running/sleeping/done) and generation depth." color="bg-purpleToken" />
                <Widget name="Status Board" desc="Counts of active, sleeping, and waiting continuations. Quick health check for the research swarm." color="bg-success" />
                <Widget name="Wake Monitor" desc="What each sleeping continuation is waiting for: timer, event, human signal, sibling publish, or data arrival." color="bg-warning" />
                <Widget name="Cost Tracker" desc="Cumulative cost by model tier (Opus/Sonnet/Haiku) and tool usage. Budget burn rate visualization." color="bg-error" />
                <Widget name="Event Log" desc="Same real-time event stream as Flightmode — shared widget, different layout context." color="bg-info" />
                <Widget name="Findings Stream" desc="Same findings view as Flightmode — shared widget." color="bg-success" />
              </div>
              <div className="p-3 bg-tower-surface border border-tower-border rounded text-xs text-tower-muted">
                <span className="text-warning font-semibold">Data source:</span> Cosmos DB containers — continuations, continuation-events, continuation-edges, continuation-leases
              </div>
            </div>
          )}

          {active === 'studio' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-success">Studio — MCP Development IDE</h1>
              <p className="text-sm text-tower-muted">Edit, test, and deploy the SQL queries and agent prompts that power MCP tools. Changes go live within 30 seconds — no code deploy, no pipeline, no restart.</p>

              <div className="p-4 bg-tower-surface border border-success/30 rounded-lg text-xs">
                <div className="font-semibold text-tower-accent mb-2">Studio Layout (3-column)</div>
                <pre className="text-tower-muted font-mono leading-relaxed">{`┌─────────────┬────────────────────┬───────────────┐
│ MCP Browser │ SQL Editor         │ Version Hx    │
│ Schema Exp. │ Query Results      │ Event Log     │
└─────────────┴────────────────────┴───────────────┘`}</pre>
              </div>

              <div className="space-y-1">
                <Widget name="MCP Browser" desc="Browse data sources (Redshift/PostgreSQL) and their SQL tools. Click a tool to load it in the SQL Editor. Shows category, param count, tables." color="bg-warning" />
                <Widget name="SQL Editor" desc="CodeMirror 6 with SQL syntax highlighting. Auto-generated parameter form from artifact metadata. Test/Save Draft/Promote buttons." color="bg-success" />
                <Widget name="Schema Explorer" desc="Browse schemas, tables, and columns inferred from artifact SQL references. Tree view: schema → table → columns." color="bg-primary" />
                <Widget name="Query Results" desc="Sortable data table showing test output. Columns, row count, execution time, CSV export. Error messages for failed queries." color="bg-info" />
                <Widget name="Version History" desc="Timeline of all artifact versions with validation status (passed/failed/pending). Promote any version or rollback to previous." color="bg-purpleToken" />
                <Widget name="Prompt Editor" desc="Edit agent system prompts with {{variable}} preview. Configure model (Opus/Sonnet/Haiku), temperature, tool bindings. Available from + Add Widget." color="bg-warning" />
              </div>

              <div className="p-4 bg-tower-surface border border-tower-border rounded-lg">
                <div className="text-sm font-semibold text-tower-accent mb-2">Try-Rebuild-Rerun Cycle</div>
                <div className="space-y-3">
                  <FlowStep num="1" title="Browse" desc="Select a SQL tool from the MCP Browser" color="bg-tower-muted" />
                  <FlowStep num="2" title="Edit" desc="Modify the SQL in the CodeMirror editor" color="bg-tower-muted" />
                  <FlowStep num="3" title="Test" desc="Fill params, click Test — results appear instantly" color="bg-success" />
                  <FlowStep num="4" title="Save" desc="Click Save Draft — creates v2, v3, etc." color="bg-primary" />
                  <FlowStep num="5" title="Promote" desc="Click Promote — agents use the new SQL within 30 seconds" color="bg-warning" />
                  <FlowStep num="6" title="Rollback" desc="If something breaks, click previous version in Timeline — reverts in 30 seconds" color="bg-error" />
                </div>
              </div>

              <div className="p-3 bg-tower-surface border border-tower-border rounded text-xs text-tower-muted">
                <span className="text-success font-semibold">Data source:</span> Cosmos DB containers — versioned-artifacts (18 SQL tools), artifact-events (audit trail), data-sources (connection registry).
                Execution targets: Amazon Redshift (dmax schema, stg database) and Azure PostgreSQL (customer schema).
              </div>
            </div>
          )}

          {active === 'dataflow' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold text-tower-accent">Data Flow Architecture</h1>

              <div className="p-4 bg-tower-surface border border-tower-border rounded-lg">
                <div className="text-sm font-semibold text-tower-accent mb-3">When an Agent Uses an MCP Tool</div>
                <div className="space-y-2">
                  <FlowStep num="1" title="User asks a question in Barnyard Chat" desc="e.g., 'Show Abe Harpster diets split by feasible/infeasible'" color="bg-primary" />
                  <FlowStep num="2" title="Agent decides to call a tool" desc="Max Dairy Analyst selects maxsense_query_diets" color="bg-purpleToken" />
                  <FlowStep num="3" title="DynamicQueryCatalog checks cache" desc="30-second TTL — if expired, re-queries Cosmos for isLive=true artifacts" color="bg-warning" />
                  <FlowStep num="4" title="SQL template loaded from Cosmos" desc="e.g., SELECT ... FROM dmax.vw_diet_optimization_bridge WHERE site_key = {{site_key}}" color="bg-warning" />
                  <FlowStep num="5" title="ReadOnlySqlGuard validates" desc="Rejects DML/DDL, enforces row cap, timeout" color="bg-error" />
                  <FlowStep num="6" title="Execute against Redshift or PostgreSQL" desc="Returns rows to agent, agent formats response for user" color="bg-success" />
                </div>
              </div>

              <div className="p-4 bg-tower-surface border border-tower-border rounded-lg">
                <div className="text-sm font-semibold text-tower-accent mb-3">When You Edit in Studio</div>
                <div className="space-y-2">
                  <FlowStep num="1" title="Edit SQL in CodeMirror" desc="Modify the query template" color="bg-success" />
                  <FlowStep num="2" title="POST /api/artifacts/{'{id}'}/test" desc="Guard validates → interpolate params → execute against live DB → return results" color="bg-primary" />
                  <FlowStep num="3" title="POST /api/artifacts/{'{id}'}/versions" desc="New version doc in Cosmos (isDraft=true). Audit event logged." color="bg-purpleToken" />
                  <FlowStep num="4" title="POST /api/artifacts/{'{id}'}/promote/{'{versionId}'}" desc="Old version isLive=false, new version isLive=true. Cache expires ≤30s." color="bg-warning" />
                  <FlowStep num="5" title="Next agent call picks up new SQL" desc="DynamicQueryCatalog reloads from Cosmos on cache miss" color="bg-success" />
                </div>
              </div>

              <div className="p-3 bg-tower-surface border border-tower-border rounded text-xs text-tower-muted">
                <span className="text-tower-accent font-semibold">Key insight:</span> The versioned artifact store is the single source of truth.
                Both the Studio (for editing) and the agents (for execution) read from the same Cosmos container.
                The 30-second DynamicQueryCatalog cache is the only delay between promote and agent pickup.
              </div>
            </div>
          )}

          {active === 'glossary' && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-tower-accent">Glossary</h1>
              <div className="space-y-2 text-sm">
                {[
                  ['Artifact', 'A versioned SQL query, agent prompt, or tool definition stored in Cosmos DB.'],
                  ['MCP', 'Model Context Protocol — the standard for how agents discover and call tools.'],
                  ['Continuation', 'A resumable, budget-aware research process that can sleep, wake, fork, and merge.'],
                  ['Journal', 'A tree of continuations sharing a root — the unit of research work in Maestro.'],
                  ['DynamicQueryCatalog', 'Service that loads SQL artifacts from Cosmos with a 30-second cache. Falls back to static code if Cosmos is empty.'],
                  ['ReadOnlySqlGuard', 'Safety layer that blocks write operations (INSERT/UPDATE/DELETE) and enforces row/byte/timeout caps.'],
                  ['Promote', 'Make a draft version the live version. Agents pick it up within 30 seconds.'],
                  ['Rollback', 'Revert to a previous version by promoting it. One click, live in 30 seconds.'],
                  ['Shadow price', 'How much diet cost changes per unit of constraint relaxation — key metric for dairy reformulation.'],
                  ['Site key', 'A version-specific surrogate key in Redshift. Each site modification creates a new one.'],
                  ['Bridge view', 'Pre-joined Redshift views (dmax.vw_*_bridge) that link dimension tables through site versions.'],
                  ['__system__ tenant', 'Special tenant ID for system-seeded artifacts visible to all users.'],
                ].map(([term, def]) => (
                  <div key={term} className="flex gap-3 py-1.5 border-b border-tower-border/30">
                    <span className="text-tower-accent font-mono w-44 shrink-0">{term}</span>
                    <span className="text-tower-muted">{def}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
